import { createStyles, FormControl, FormControlLabel, IconButton, makeStyles, Radio, RadioGroup, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, DownIcon, IconButtonWrapper, UpIcon } from '../Common';
import { ChartFilter } from '../ChartComponents';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { convertStringNumberToNumber, ETimePeriod, formatNumber, formatToDatePeriod, getLabels, getMaxTickLimit, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { DrillDownType } from '../DrillDownType';
import { SelectedItemProps } from '../MattersByStatusChart';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import MTooltip from '@material-ui/core/Tooltip';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import { useScreenshot, createFileName } from 'use-react-screenshot';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import { useCurrencySymbol } from '../../hooks/useCurrencySymbol';
import VideoLibraryOutlinedIcon from '@material-ui/icons/VideoLibraryOutlined';
import { VideoModal } from '../../components/VideoModal';
import { ChartInfo } from '../../components/ChartInfo';

import { getBudgetProgressColor, RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvLegendLabels, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { ChartEmptyState } from '../ChartEmptyState';
import { ChartNotes } from '../ChartNotes';
import InsertChartOutlinedIcon from '@material-ui/icons/InsertChartOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
            padding: '0 15px',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px',
            '& .title': {
                fontSize: 16,
            },
            '& .colWrapper': {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: 15,
                flexFlow: 'row wrap',
            },
            '& .col': {
                fontSize: '16px',
                fontWeight: 600,
                textAlign: 'center',
                border: '1px solid',
                padding: '15px',
                display: 'flex',
                margin: '0 10px 10px 10px',
                marginBottom: '10px',
                flexFlow: 'column',
                flex: 1,
                '& .value': {
                    fontSize: '36px',
                    padding: '20px 0',
                },
                '& .sub': {
                    display: 'flex',
                    flexFlow: 'row wrap',
                    justifyContent: 'center',
                    alignItems: 'center',
                },
                '& .status': {
                    marginLeft: '10px',
                    display: 'flex',
                    flexFlow: 'row wrap',
                    alignItems: 'center',
                },
            },
            '& .increase': {
                color: 'green',
            },
            '& .decrease': {
                color: 'red',
            },
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            '& .titleWrapper': {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                '& .title': {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .value': {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .secondary-title': {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            position: 'relative',
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 10,
        },
        span: {
            fontSize: '1rem',
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            display: 'flex',
            alignItems: 'center',
            margin: '0 -2.5px',
            '& > *': {
                margin: '0 2.5px',
            },
            '@media print': {
                display: 'none !important',
            },
        },
        learnIcon: {
            color: 'rgb(0, 120, 212)'
        },
        scrollUp: {
            display: 'none',
            position: 'absolute',
            top: '30px',
            justifyContent: 'center',
            transform: 'translateX(-50%)',
            left: '50%',
            minWidth: '54px',
        },
        scrollDown: {
            display: 'none',
            position: 'absolute',
            top: '50px',
            justifyContent: 'center',
            transform: 'translateX(-50%)',
            left: '50%',
            minWidth: '54px',
        }
    }),
);

export enum OptionsEnum {
    MONTH,
    PREVIOUS_MONTH,
    FINANCIAL_YEAR,
    PREV_FINANCIAL_YEAR,
}

interface FeesBilledVsBudgetByGroupChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
    infoUrl: string | null;
    groupBy: 'Office' | 'Department' | 'Team';
}

interface ChartState {
    options: OptionsEnum;
    months: string[];
    counts: number[];
    budgetCounts: number[];
    currentYearTotal: number;
    currentTotal: number;

    budgetTotal: number;
    varianceTotal: number;
    percentageTotal: number;

    budgetYearTotal: number;
    varianceYearTotal: number;
    percentageYearTotal: number;

    leftPeriodString: string;
    rightPeriodString: string;
}

export const FeesBilledVsBudgetByGroupChart: React.FC<FeesBilledVsBudgetByGroupChartProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const entireChartRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const scrollUpAndPrevRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const scrollDownAndNextRef = React.useRef<any>();

    const [isVideoModalOpen, setIsVideoModalOpen] = useState<boolean>(false);

    const feesBilledVsBudget = useQuery<FeesBilledVsBudgetByOfficeQueryData, FeesBilledVsBudgetByOfficeQueryParams>(FeesBilledVsBudgetByOfficeQuery, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility,
            showAllRecords: props.showAllRecords,
            groupBy: props.groupBy
        },
    });

    const takeScreenShot = useScreenshot({
        type: 'image/jpeg',
        quality: 1.0
    })[1];

    const [state, setState] = useState<ChartState>({
        options: props.maximizeSelected ? props.maximizeSelected.options : OptionsEnum.MONTH,
        months: [],
        counts: [],
        budgetCounts: [],
        currentYearTotal: 0,
        currentTotal: 0,
        budgetTotal: 0,
        varianceTotal: 0,
        percentageTotal: 0,
        budgetYearTotal: 0,
        varianceYearTotal: 0,
        percentageYearTotal: 0,
        leftPeriodString: formatToDatePeriod('monthToDate'),
        rightPeriodString: formatToDatePeriod('yearToDateFees'),
    });

    const currency = useCurrencySymbol();

    useEffect(() => {
        window.addEventListener('keydown', onKeyDownHandler);

        return () => {
            window.removeEventListener('keydown', onKeyDownHandler);
        };
        // tslint:disable-next-line: align
    }, []);

    // tslint:disable-next-line:no-any
    const onKeyDownHandler = (event: any) => {
        if (event.keyCode === 80 && (event.ctrlKey || event.metaKey) && !event.altKey) {
            event.preventDefault();
            if (event.stopImmediatePropagation) {
                event.stopImmediatePropagation();
            } else {
                event.stopPropagation();
            }
            return;
        }
    };

    useEffect(() => {
        setState((prevState) => {
            let months: string[] = [];
            let counts: number[] = [];
            let budgetCounts: number[] = [];
            let currentTotal: number = 0;
            let currentYearTotal: number = 0;
            let leftPeriodString: string = '';
            let rightPeriodString: string = formatToDatePeriod('yearToDateFees');

            let budgetTotal = 0;
            let varianceTotal = 0;
            let percentageTotal = 0;
            let budgetYearTotal = 0;
            let varianceYearTotal = 0;
            let percentageYearTotal = 0;

            if (
                !feesBilledVsBudget.loading &&
                !feesBilledVsBudget.error &&
                feesBilledVsBudget.data &&
                feesBilledVsBudget.data.statistics &&
                feesBilledVsBudget.data.statistics.fees && feesBilledVsBudget.data.statistics.fees.feesBilledVsBudgetByGrouping
            ) {
                var {
                    lastMonth,
                    previousMonth,
                    lastFinancialYear,
                    previousFinancialYear,
                } = feesBilledVsBudget.data.statistics.fees.feesBilledVsBudgetByGrouping;

                currentYearTotal = lastFinancialYear.summary.lastPeriodTotal;
                budgetYearTotal = lastFinancialYear.summary.budget;
                varianceYearTotal = lastFinancialYear.summary.variance;
                percentageYearTotal = Math.round(lastFinancialYear.summary.percentage);

                if (state.options === OptionsEnum.MONTH) {
                    months = lastMonth.feesBilledVsBudgetGrouping.map((s) => s.name);
                    counts = lastMonth.feesBilledVsBudgetGrouping.map((s) => s.billing);
                    budgetCounts = lastMonth.feesBilledVsBudgetGrouping.map((s) => s.budget);

                    currentTotal = lastMonth.summary.lastPeriodTotal;
                    budgetTotal = lastMonth.summary.budget;
                    varianceTotal = lastMonth.summary.variance;
                    percentageTotal = Math.round(lastMonth.summary.percentage);
                    leftPeriodString = formatToDatePeriod('monthToDate');
                } else if (state.options === OptionsEnum.PREVIOUS_MONTH) {
                    months = previousMonth.feesBilledVsBudgetGrouping.map((s) => s.name);
                    counts = previousMonth.feesBilledVsBudgetGrouping.map((s) => s.billing);
                    budgetCounts = previousMonth.feesBilledVsBudgetGrouping.map((s) => s.budget);

                    currentTotal = previousMonth.summary.lastPeriodTotal;
                    budgetTotal = previousMonth.summary.budget;
                    varianceTotal = previousMonth.summary.variance;
                    percentageTotal = Math.round(previousMonth.summary.percentage);

                    if (previousMonth.financialYearToPreviousMonthSummary) {
                        currentYearTotal = previousMonth.financialYearToPreviousMonthSummary.lastPeriodTotal;
                        budgetYearTotal = previousMonth.financialYearToPreviousMonthSummary.budget;
                        varianceYearTotal = previousMonth.financialYearToPreviousMonthSummary.variance;
                        percentageYearTotal = Math.round(previousMonth.financialYearToPreviousMonthSummary.percentage);
                    } else {
                        currentYearTotal = 0;
                        budgetYearTotal = 0;
                        varianceYearTotal = 0;
                        percentageYearTotal = 0;
                    }

                    leftPeriodString = formatToDatePeriod('previousMonth');
                    rightPeriodString = formatToDatePeriod('previousMonthYearToDateFees');
                } else if (state.options === OptionsEnum.FINANCIAL_YEAR) {
                    months = lastFinancialYear.feesBilledVsBudgetGrouping.map((s) => s.name);
                    counts = lastFinancialYear.feesBilledVsBudgetGrouping.map((s) => s.billing);
                    budgetCounts = lastFinancialYear.feesBilledVsBudgetGrouping.map((s) => s.budget);

                    currentTotal = lastFinancialYear.summary.lastPeriodTotal;
                    budgetTotal = lastFinancialYear.summary.budget;
                    varianceTotal = lastFinancialYear.summary.variance;
                    percentageTotal = Math.round(lastFinancialYear.summary.percentage);
                    leftPeriodString = formatToDatePeriod('yearToDate');
                } else {
                    months = previousFinancialYear.feesBilledVsBudgetGrouping.map((s) => s.name);
                    counts = previousFinancialYear.feesBilledVsBudgetGrouping.map((s) => s.billing);
                    budgetCounts = previousFinancialYear.feesBilledVsBudgetGrouping.map((s) => s.budget);

                    currentTotal = previousFinancialYear.summary.lastPeriodTotal;
                    budgetTotal = previousFinancialYear.summary.budget;
                    varianceTotal = previousFinancialYear.summary.variance;
                    percentageTotal = Math.round(previousFinancialYear.summary.percentage);
                    leftPeriodString = formatToDatePeriod('previousYearToDate');
                }
            }

            return {
                ...prevState,
                months,
                counts,
                budgetCounts,
                currentTotal,
                currentYearTotal,
                budgetTotal,
                varianceTotal,
                budgetYearTotal,
                varianceYearTotal,
                percentageTotal,
                percentageYearTotal,
                leftPeriodString,
                rightPeriodString,
            };
        });
    // tslint:disable-next-line: align
    }, [state.options, feesBilledVsBudget.loading, feesBilledVsBudget.error, feesBilledVsBudget.data]);

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart('isFeesBilledVsBudgetByGroupChartOpen');
        }
    };

    const onChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => {
            return {
                ...prevState,
                // tslint:disable-next-line: radix
                options: parseInt((event.target as HTMLInputElement).value),
            };
        });
    };

    const handleTotalClick = (isMonth: Boolean) => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();
            let defaultView = 'Fees Billed MTD/YTD';
            let timePeriod = ETimePeriod.THIS_MONTH;

            if (isMonth) {
                if (state.options === OptionsEnum.MONTH) {
                    searchCriterias.push({ property: 'Month', operator: '', comparison: '', period: 'curr' });
                    timePeriod = ETimePeriod.THIS_MONTH;
                } else if (state.options === OptionsEnum.PREVIOUS_MONTH) {
                    searchCriterias.push({ property: 'Month', operator: '', comparison: '', period: 'prev' });
                    defaultView = 'Fees Billed PM/PYTD';
                    timePeriod = ETimePeriod.PREVIOUS_MONTH;
                } else if (state.options === OptionsEnum.FINANCIAL_YEAR) {
                    searchCriterias.push({ property: 'FinancialYearToDate', operator: '', comparison: '', period: 'curr' });
                    timePeriod = ETimePeriod.FINANCIAL_YEAR;
                } else if (state.options === OptionsEnum.PREV_FINANCIAL_YEAR) {
                    searchCriterias.push({ property: 'FinancialYearToDate', operator: '', comparison: '', period: 'prev' });
                    defaultView = 'Fees Billed PM/PYTD';
                    timePeriod = ETimePeriod.PREVIOUS_FINANCIAL_YEAR;
                }
            } else {
                var period = 'curr';
                timePeriod = ETimePeriod.FINANCIAL_YEAR;
                if (state.options === OptionsEnum.PREVIOUS_MONTH) {
                    period = 'prevMonth';
                    defaultView = 'Fees Billed PM/PYTD';
                    timePeriod = ETimePeriod.PREVIOUS_MONTH;
                }

                searchCriterias.push({ property: 'FinancialYearToDate', operator: '', comparison: '', period: period });
            }

            const title = `Drilldown from Fees Billed vs Budget By ${props.groupBy} for ${timePeriod}`;

            props.getSelectedItem(searchCriterias, false, title, DrillDownType.staffBillingSummary, defaultView);
        }
    };

    const onPan = (context: { chart: Chart }) => {
        chartRef.current.options.scales.y.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };

    const onMaximize = () => {
        if (props.onMaximizeSelected) {
            props.onMaximizeSelected({
                options: state.options,
            });
        }
    };

    const onToggleVideo = () => {
        setIsVideoModalOpen(!isVideoModalOpen);
    };

    // tslint:disable-next-line:no-any
    const onDownload = (name: string, extension: string = 'jpg') => (image: any) => {
        const a = document.createElement('a');
        a.href = image;
        a.download = createFileName(extension, name);
        a.click();
    };

    const onDownloadScreenshot = () => {
        setTimeout(() => {
            takeScreenShot(entireChartRef.current).then(onDownload(`Fees Billed vs Budget By ${props.groupBy}`));
        // tslint:disable-next-line: align
        }, 500);
    };

    const onScrollUpAndPrev = (type: 'up' | 'prev') => {
        if (chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min > 0) {
            const scrollRatio = chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].max - chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min;
            const min = chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min;
            if (scrollRatio < chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min) {
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min -= scrollRatio ? scrollRatio : 1;
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].max -= scrollRatio ? scrollRatio : 1; 
            } else {
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min -= min;
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].max -= min;
            }
            chartRef.current.options.scales[type === 'up' ? 'x' : 'y'].min = 0;
            chartRef.current.update();
            // enabled scroll up
            scrollUpAndPrevRef.current.classList.remove('Mui-disabled');
        } else {
            // disabled scroll up
            scrollUpAndPrevRef.current.classList.add('Mui-disabled');
        }
        scrollDownAndNextRef.current.classList.remove('Mui-disabled');
    };
    
    const onScrollDownAndNext = (type: 'down' | 'next') => {

        const labels = state.months;

        if (chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max < labels.length) {
            const remainingBar = labels.length - chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max;
            const scrollRatio = chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max - chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].min;
            if (remainingBar <= scrollRatio) {
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].min += remainingBar;
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max += remainingBar;
            } else {
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].min += scrollRatio ? scrollRatio : 1;
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max += scrollRatio ? scrollRatio : 1;
            }
            chartRef.current.options.scales[type === 'down' ? 'x' : 'y'].min = 0;
            chartRef.current.update();
            // enabled scroll down
            scrollDownAndNextRef.current.classList.remove('Mui-disabled');
        } else {
            // disabled scroll down
            scrollDownAndNextRef.current.classList.add('Mui-disabled');
        }
        scrollUpAndPrevRef.current.classList.remove('Mui-disabled');
    };

    const onScollBar = (type: 'up' | 'down' | 'prev' | 'next') => () => {
        if (type === 'up') {
            onScrollUpAndPrev('up');
        } else if (type === 'down') {
            onScrollDownAndNext('down');
        }  else if (type === 'next') {
            onScrollDownAndNext('next');
        } else {
            onScrollUpAndPrev('prev');
        } 
    };

    const onDisabledEnabledArrow = (barType: 'horizontal' | 'vertical') => {  
        const labels = state.months;
        // up 
        if (chartRef.current.options.scales[barType === 'horizontal' ? 'y' : 'x'].min > 0) {
            scrollUpAndPrevRef.current.classList.remove('Mui-disabled');
        } else {
            scrollUpAndPrevRef.current.classList.add('Mui-disabled');
        }

        // down
        if (chartRef.current.options.scales[barType === 'horizontal' ? 'y' : 'x'].max < labels.length) {
            scrollDownAndNextRef.current.classList.remove('Mui-disabled');
        } else {
            scrollDownAndNextRef.current.classList.add('Mui-disabled');
        }
    };
    
    const onZoom = (context: { chart: Chart }) => {

        onDisabledEnabledArrow('vertical');

        chartRef.current.options.scales.x.min = 0;
        chartRef.current.update();
    };

    // tslint:disable-next-line: no-any
    const onContextMenu = (event: any) => {
        event.preventDefault();
        if (event.button !== 2) {
            return false;
        }
        return true;
    };

    const chartControllerComponent = () => {
        return (
            <>
                <IconButtonWrapper aria-label="close">
                    <div
                        className={`${classes.helpIcon} rv-chart-help`} 
                    >
                        <ChartInfo 
                            description={props.description}
                            fontSize={props.showAllRecords ? 'large' : 'medium'}
                            // className={classes.helpIcon}
                        />
                        {props.infoUrl && (
                            <MTooltip title="Learn more">
                                <IconButton className={classes.learnIcon} aria-label="maximize" onClick={onToggleVideo} size={props.showAllRecords ? 'medium' : 'small'}>
                                    <VideoLibraryOutlinedIcon />
                                </IconButton>
                            </MTooltip>
                        )}
                    </div>
                    {props.showAllRecords && (
                        <IconButton size="medium" onClick={onDownloadScreenshot}>
                            <GetAppOutlinedIcon color="primary" fontSize="large" />
                        </IconButton>
                    )}
                    {!props.showAllRecords && props.onMaximizeSelected && (
                        <MTooltip title="Expand chart">
                            <IconButton aria-label="maximize" onClick={onMaximize} size="small">
                                <AspectRatioIcon />
                            </IconButton>
                        </MTooltip>
                    )}
                    <IconButton aria-label="close" onClick={onCloseChart} size={props.showAllRecords ? 'medium' : 'small'}>
                        <CloseIcon className={classes.closeIcon} fontSize={props.showAllRecords ? 'large' : 'medium'} />
                    </IconButton>
                </IconButtonWrapper>
            </>
        );
    };
    
    if (feesBilledVsBudget.loading || currency.loading) {
        return (
            <div className="rv-chart rv-chart-loading">
                {chartControllerComponent()}
                <ChartSkeleton variant="bars" />
            </div>
        );
    }

    const symbol = currency.data ? currency.data.options.currencySymbol.symbol : '$';

    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.counts, state.budgetCounts) ? ' rv-chart--empty' : ''}`} ref={entireChartRef}>
            {isVideoModalOpen && props.infoUrl && (
                <VideoModal 
                    open={isVideoModalOpen}
                    onClose={onToggleVideo}
                    url={props.infoUrl}
                />
            )}
            {chartControllerComponent()}
            <div className={classes.titleContainer}>
                <ChartHeading icon={<InsertChartOutlinedIcon />} title={<>{`Fees Billed vs Budget By ${props.groupBy}`}</>} />
                <div className="colWrapper">
                    <div className="col">
                        <div className="title">{state.leftPeriodString}</div>
                        <div className={`${classes.totalDiv} value`} onClick={() => handleTotalClick(true)}>
                            <AnimatedNumber value={state.currentTotal} format={(amount) => `${symbol}${formatNumber(amount)}`} />
                        </div>
                        <div className="rv-meter">
                            <i style={{ width: `${Math.min(100, Math.max(0, state.percentageTotal))}%`, backgroundColor: getBudgetProgressColor(state.percentageTotal) }} />
                        </div>
                        <div className={`${classes.totalDiv} rv-budget`} onClick={() => handleTotalClick(true)}>
                            {`Budget ${symbol}${formatNumber(state.budgetTotal)}`}
                        </div>
                        <div className="rv-stats">
                            <div className={`${classes.totalDiv} rv-pct`} onClick={() => handleTotalClick(true)}>
                                {`${formatNumber(state.percentageTotal)}% achvd`}
                            </div>
                            <div className={`rv-variance ${state.budgetTotal < state.currentTotal ? 'good' : 'bad'}`} onClick={() => handleTotalClick(true)} title="Variance">
                                {state.budgetTotal < state.currentTotal ? <UpIcon className="increase" /> : <DownIcon className="decrease" />}
                                {`${symbol}${formatNumber(state.varianceTotal)}`}
                            </div>
                        </div>
                    </div>
                    <div className="col">
                        <div className="title">{state.rightPeriodString}</div>
                        <div className={`${classes.totalDiv} value`} onClick={() => handleTotalClick(false)}>
                            <AnimatedNumber value={state.currentYearTotal} format={(amount) => `${symbol}${formatNumber(amount)}`} />
                        </div>
                        <div className="rv-meter">
                            <i style={{ width: `${Math.min(100, Math.max(0, state.percentageYearTotal))}%`, backgroundColor: getBudgetProgressColor(state.percentageYearTotal) }} />
                        </div>
                        <div className={`${classes.totalDiv} rv-budget`} onClick={() => handleTotalClick(false)}>
                            {`Budget ${symbol}${formatNumber(state.budgetYearTotal)}`}
                        </div>
                        <div className="rv-stats">
                            <div className={`${classes.totalDiv} rv-pct`} onClick={() => handleTotalClick(false)}>
                                {`${formatNumber(state.percentageYearTotal)}% achvd`}
                            </div>
                            <div className={`rv-variance ${state.budgetYearTotal < state.currentYearTotal ? 'good' : 'bad'}`} onClick={() => handleTotalClick(false)} title="Variance">
                                {state.budgetYearTotal < state.currentYearTotal ? <UpIcon className="increase" /> : <DownIcon className="decrease" />}
                                {`${symbol}${formatNumber(state.varianceYearTotal)}`}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={classes.barContainer} style={{ maxHeight: props.showAllRecords ? '100%' : '350px' }}>
                {props.showAllRecords && (
                    <>
                        <div className={`${classes.scrollUp} scroll`}>
                            <IconButton 
                                size="medium" 
                                onClick={onScollBar('up')}
                                ref={scrollUpAndPrevRef}
                            >
                                <KeyboardArrowUpIcon color="primary" fontSize="large" />
                            </IconButton>
                        </div>
                        <div className={`${classes.scrollDown} scroll`}>
                            <IconButton 
                                size="medium" 
                                onClick={onScollBar('down')}
                                ref={scrollDownAndNextRef}
                            >
                                <KeyboardArrowDownIcon color="primary" fontSize="large" />
                            </IconButton>
                        </div>
                    </>
                )}
                <Bar
                    ref={chartRef}
                    onContextMenu={onContextMenu}
                    // tslint:disable-next-line: no-any
                    onClick={(event: React.MouseEvent<any>) => {
                        const _data = getElementAtEvent(chartRef.current, event);
                        if (_data.length > 0 && props.getSelectedItem) {
                            // const _index = getElementAtEvent(chartRef.current, event)[0].index;

                            // const item = state.months[_index];

                            // const searchCriterias = new Array<SearchCriteria>();

                            // if (state.options === OptionsEnum.MONTH) {
                            //     searchCriterias.push(
                            //         { property: 'DateShortString', operator: 'eq', comparison: item, period: 'curr' }, // e.g. May 05
                            //     );
                            // } else if (state.options === OptionsEnum.PREVIOUS_MONTH) {
                            //     searchCriterias.push(
                            //         { property: 'DateShortString', operator: 'eq', comparison: item, period: 'prev' }, // e.g. Apr 05
                            //     );
                            // } else if (state.options === OptionsEnum.FINANCIAL_YEAR) {
                            //     searchCriterias.push(
                            //         { property: 'Month', operator: 'eq', comparison: item, period: 'curr' }, // e.g. May
                            //     );
                            // } else {
                            //     searchCriterias.push(
                            //         { property: 'Month', operator: 'eq', comparison: item, period: 'prev' }
                            //     );
                            // }

                            // props.getSelectedItem(
                            //     searchCriterias,
                            //     false,
                            //     'Fees Billed vs Budget - ' + item,
                            //     DrillDownType.staffBillingSummary,
                            //     state.options === OptionsEnum.MONTH
                            //         ? 'Fees Billed WTD'
                            //         : 'Fees Billed MTD/YTD'
                            // );
                        }
                    }}
                    data={{
                        labels: getLabels(state.months),
                        datasets: [
                            {
                                label: 'Fees Billed',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                                data: state.counts,
                            },
                            {
                                label: 'Budget',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primarySoft),
                                data: state.budgetCounts,
                            }
                        // tslint:disable-next-line:no-any
                        ] as any,
                    }}
                    options={{
                        onHover: onHover,
                        indexAxis: 'y' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            x: {
                                grid: rvValueAxisStyle.grid,
                                // beginAtZero: true,
                                suggestedMax: getSuggestedMaxTick([...state.budgetCounts, ...state.counts]),
                                beginAtZero: true,
                                ticks: {
                                    ...rvValueAxisStyle.ticks,
                                    maxTicksLimit: getMaxTickLimit([...state.budgetCounts, ...state.counts]),
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${symbol}${formatNumber(tickValue)}`;
                                    },
                                },
                            },
                            y: {
                                grid: rvCategoryAxisStyle.grid,
                                // min: 0,
                                // max: 9 // minimum display
                                ticks: {
                                    ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false
                                }
                            }
                        },
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: true,
                                labels: rvLegendLabels,
                            },
                            title: {
                                display: false,
                                text: ``,
                            },
                            zoom: {
                                pan: {
                                    enabled: false,
                                    onPan: onPan,
                                },
                                zoom: {
                                    wheel: {
                                        enabled: props.showAllRecords,
                                    },
                                    onZoom: onZoom,
                                },
                                // limits: {
                                //   y: { min: 0, max: 9 },
                                // },
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: function (context: any) {
                                        const {
                                            //   dataIndex,
                                            //   label,
                                            formattedValue,
                                            dataset,
                                        } = context;

                                        var currentValue = convertStringNumberToNumber(formattedValue);

                                        return `${dataset.label}: ${symbol}${formatNumber(currentValue)}`;
                                    },
                                },
                            },
                        },
                    }}
                />
                {isChartDataEmpty(state.counts, state.budgetCounts) && (
                    <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                )}
            </div>
            <FormControl component="div">
                <RadioGroup name="last" value={state.options} onChange={onChangeRadio} className={classes.radioGroup}>
                    <FormControlLabel value={OptionsEnum.MONTH} control={<Radio color="primary" />} label="This Month" />
                    <FormControlLabel value={OptionsEnum.PREVIOUS_MONTH} control={<Radio color="primary" />} label="Previous Month" />
                    <FormControlLabel value={OptionsEnum.FINANCIAL_YEAR} control={<Radio color="primary" />} label="Financial Year" />
                    <FormControlLabel value={OptionsEnum.PREV_FINANCIAL_YEAR} control={<Radio color="primary" />} label="Previous Financial Year" />
                </RadioGroup>
            </FormControl>
            <ChartNotes notes={['Information collated based on Timekeeper']} actionHint="Drill down to timekeeper details" />
        </div>
    );
};

// tslint:disable-next-line:no-any
export interface CurrencySymbolData {
    options: Options;
}

export interface Options {
    currencySymbol: CurrencySymbol;
}

export interface CurrencySymbol {
    symbol: string;
}

export const CURRENCY_SYMBOL_QUERY = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;

export type FeesBilledVsBudgetByOfficeQueryParams = {
    entityType?: number;
    entityGuid?: string;
    responsibility?: number;
    showAllRecords?: boolean;
    groupBy?: 'Office' | 'Department' | 'Team';
};

export type FeesBilledVsBudgetByOfficeQueryData = {
    statistics: Statistics;
};

export type Statistics = {
    fees: Fees;
};

export type Fees = {
    feesBilledVsBudgetByGrouping: FeesBilledVsBudgetByGrouping;
};

export type FeesBilledVsBudgetByGrouping = {
    lastFinancialYear:     LastFinancialYear;
    lastMonth:             LastFinancialYear;
    previousMonth:         PreviousMonth;
    previousFinancialYear: LastFinancialYear;
};

export type LastFinancialYear = {
    summary:                    Summary;
    feesBilledVsBudgetGrouping: FeesBilledVsBudgetGrouping[];
};

export type FeesBilledVsBudgetGrouping = {
    name:    string;
    budget:  number;
    billing: number;
};

export type Summary = {
    lastPeriodTotal:     number;
    previousPeriodTotal: number;
    budget:              number;
    percentage:          number;
    variance:            number;
};

export type PreviousMonth = {
    summary:                             Summary;
    feesBilledVsBudgetGrouping:          FeesBilledVsBudgetGrouping[];
    financialYearToPreviousMonthSummary: Summary;
};

const FeesBilledVsBudgetByOfficeQuery = gql`
    fragment FeesBilledInPeriodFragment on FeesBilledInPeriod {
        summary {
            lastPeriodTotal
            previousPeriodTotal
            budget
            percentage
            variance
        }
        feesBilledVsBudgetGrouping {
            name
            budget
            billing
        }
    }

    query FeesBilledVsBudgetByOfficeData($entityType: Int, $entityGuid: String, $responsibility: Int, $showAllRecords: Boolean, $groupBy: FeesBilledVsBudgetGroupBy) {
        statistics {
            fees {
                feesBilledVsBudgetByGrouping(
                    entityType: $entityType
                    entityGuid: $entityGuid
                    responsibility: $responsibility
                    showAllRecords: $showAllRecords
                    groupBy: $groupBy
                ) {
                    lastFinancialYear {
                        ...FeesBilledInPeriodFragment
                    }
                    lastMonth {
                        ...FeesBilledInPeriodFragment
                    }
                    previousMonth {
                        ...FeesBilledInPeriodFragment
                        financialYearToPreviousMonthSummary {
                            lastPeriodTotal
                            previousPeriodTotal
                            budget
                            percentage
                            variance
                        }
                    }
                    previousFinancialYear {
                        ...FeesBilledInPeriodFragment
                    }
                }
            }
        }
    }
`;
